import {useEditor} from '@frontapp/editor-html/lib/react';
import {Trans} from '@lingui/macro';
import React, {forwardRef, Ref, useImperativeHandle, useMemo} from 'react';
import styled from 'styled-components';

import {KeyboardActionsEnum} from '../../../../libs/shared/helpers/src/browser/keyboardHelpers';
import {useInitialEffect} from '../../../../libs/shared/helpers/src/react/hooks/useEffectHelpers';
import {HandlerOf, Renderer} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {
  BoxedInput,
  BoxedInputInheritableProps,
} from '../../../../libs/shared/ui/src/components/boxedInputs/boxedInput';
import {ToolbarButton} from '../../../../libs/shared/ui/src/components/buttons/toolbarButton';
import {KeyboardShortcuts} from '../../../../libs/shared/ui/src/components/keyboard/keyboardShortcuts';
import {useAppSelector} from '../../../helpers/reduxHelpers';
import {getEmojiValidationMap} from '../../../store/stateless/emojiSelectors';
import {HtmlEditorChangeManager} from '../editor/change/htmlEditorChangeManager';
import {
  ContentEditableHtmlEditor,
  createContentEditableHtmlEditor,
} from '../editor/html/contentEditableHtmlEditor';
import {ServiceSubscription} from '../editor/serviceSubscription';
import {HtmlStyle} from '../editor/style/htmlStyle';
import {BoxedInputContentEditable} from './boxedInputContentEditable';
import {BoxedInputToolbar} from './boxedInputToolbar';

/*
 * Props.
 */

export interface HtmlEditorBoxProps extends BoxedInputInheritableProps {
  /** Sanitized HTML. */
  initialCleanHtml: string;
  /** Paste As Text preference; use HtmlEditorBoxContainer to populate this. */
  shouldPasteAsText: boolean;
  /** Whether to show a formatting toolbar. */
  hasToolbar?: boolean;
  /** To make forwardRef work in HtmlEditorBoxContainer. */
  handleRef?: Ref<HtmlEditorBoxHandle>;
  /** To optionally render a placeholder in the box input. */
  renderPlaceholder?: Renderer;
  /** To react on change within the editor. */
  onChange?: HandlerOf<boolean>;
}

/*
 * API.
 */

/** The new composer is an uncontrolled input; use this interface to get its value. */
export interface HtmlEditorBoxHandle {
  readonly cleanHtml: string;
  readonly isEmpty: boolean;
  readonly setCleanHtml: (text: string) => void;
}

/*
 * Style.
 */

const StyledWrapperDiv = styled.div`
  display: flex;
  flex-direction: column-reverse;
  min-width: 0px;
`;

/*
 * Component.
 */

/** Simplified wrapper around the new composer for use as a boxed input. */
export const HtmlEditorBox = forwardRef<HtmlEditorBoxHandle, HtmlEditorBoxProps>((props, ref) => {
  const {initialCleanHtml, shouldPasteAsText, hasToolbar, handleRef, onChange, ...inheritableProps} = props;

  const emojiValidationMap = useAppSelector(getEmojiValidationMap);
  const editor = useEditor(
    () =>
      createContentEditableHtmlEditor({
        shouldPasteAsText,
        shouldNativeDelete: true,
        initialHtml: initialCleanHtml,
        isInitialHtmlClean: true,
        hasNewDeleteSelection: true,
        emojiValidationMap,
      }),
    [],
  );

  useInitialEffect(() => {
    if (!onChange) {
      return undefined;
    }

    const editorChangeManager = new HtmlEditorChangeManager(editor);
    const unsubscribe = editorChangeManager.subscribe(() => onChange(editor.services.empty.isEmpty));
    return unsubscribe;
  });

  const handle = useMemo(
    () => ({
      get cleanHtml() {
        return editor.html;
      },
      get isEmpty() {
        return editor.services.empty.isEmpty;
      },
      setCleanHtml(html: string) {
        return editor.setCleanHtml(html);
      },
    }),
    [editor],
  );
  useImperativeHandle(ref, () => handle, [handle]);
  useImperativeHandle(handleRef, () => handle, [handle]);

  return (
    <BoxedInput {...inheritableProps}>
      {(childProps) => (
        <StyledWrapperDiv>
          <BoxedInputContentEditable {...childProps} {...inheritableProps} editor={editor} />
          {hasToolbar && (
            <BoxedInputToolbar {...childProps}>{renderHtmlStyleToolbarButtons(editor)}</BoxedInputToolbar>
          )}
        </StyledWrapperDiv>
      )}
    </BoxedInput>
  );
});

function renderHtmlStyleToolbarButtons(editor: ContentEditableHtmlEditor) {
  const focusService = editor.services.focus;
  const htmlStyleService = editor.services.htmlStyle;

  const updateStyle = (callback: (style: HtmlStyle) => HtmlStyle) => {
    const prevStyle = htmlStyleService.style;
    const nextStyle = callback(prevStyle);
    htmlStyleService.applyStyle(nextStyle);
    editor.focus();
  };

  const onResetClick = () => {
    htmlStyleService.resetStyle();
    editor.focus();
  };

  const onBoldClick = () => {
    updateStyle(({isBold}) => ({isBold: !isBold}));
  };

  const onItalicClick = () => {
    updateStyle(({isItalic}) => ({isItalic: !isItalic}));
  };

  const onUnderlineClick = () => {
    updateStyle(({hasUnderline}) => ({hasUnderline: !hasUnderline}));
  };

  const keyboardHandlers = {
    [KeyboardActionsEnum.EDITOR_BOLD]: onBoldClick,
    [KeyboardActionsEnum.EDITOR_ITALIC]: onItalicClick,
    [KeyboardActionsEnum.EDITOR_UNDERLINE]: onUnderlineClick,
  };

  return (
    <>
      <ServiceSubscription source={focusService}>
        {({isActive}) => <KeyboardShortcuts handlers={keyboardHandlers} isDisabled={!isActive} />}
      </ServiceSubscription>

      <ServiceSubscription source={htmlStyleService}>
        {({style}) => (
          <>
            <ToolbarButton
              iconName="formattingBoldSmall"
              label={<Trans id="htmleditorbox-bold">Bold</Trans>}
              keyboardAction={KeyboardActionsEnum.EDITOR_BOLD}
              isActive={style.isBold}
              onClick={onBoldClick}
              shouldPreventMouseDown
            />

            <ToolbarButton
              iconName="formattingItalicSmall"
              label={<Trans id="htmleditorbox-italic">Italic</Trans>}
              keyboardAction={KeyboardActionsEnum.EDITOR_ITALIC}
              isActive={style.isItalic}
              onClick={onItalicClick}
              shouldPreventMouseDown
            />

            <ToolbarButton
              iconName="formattingUnderlineSmall"
              label={<Trans id="htmleditorbox-underline">Underline</Trans>}
              keyboardAction={KeyboardActionsEnum.EDITOR_UNDERLINE}
              isActive={style.hasUnderline}
              onClick={onUnderlineClick}
              shouldPreventMouseDown
            />
          </>
        )}
      </ServiceSubscription>

      <ToolbarButton
        iconName="formattingRemoveSmall"
        onClick={onResetClick}
        label={<Trans id="htmleditorbox-resetstyle">Reset style</Trans>}
        shouldPreventMouseDown
      />
    </>
  );
}
